/**
 * `POST /v1/proxies/check`: connect through a proxy from the cloud and report
 * whether it works and where it exits.
 *
 * A proxy that fails is still a successful check, so failures come back as
 * `ok: false` with a friendly `error` and a machine-readable `errorCode`, not
 * as an HTTP error. HTTP errors are reserved for bad input, auth, and rate
 * limits.
 */

import { z } from "zod";
import { ProxyTypeSchema } from "./primitives.ts";

export const ProxyCheckRequestSchema = z
	.object({
		type: ProxyTypeSchema,
		host: z
			.union([z.ipv4(), z.ipv6(), z.hostname()])
			.describe("Proxy host: a DNS name, IPv4, or unbracketed IPv6 address"),
		port: z.coerce.number().int().min(1).max(65535).describe("Proxy port"),
		// RFC 1929 caps SOCKS5 credentials at 255 bytes each.
		username: z.string().max(255).optional().describe("Proxy username"),
		password: z.string().max(255).optional().describe("Proxy password"),
	})
	.describe("Proxy to check");

export type ProxyCheckRequest = z.infer<typeof ProxyCheckRequestSchema>;

export const PROXY_CHECK_ERROR_CODES = [
	"blocked_address",
	"dns_failure",
	"connection_refused",
	"timeout",
	"auth_failed",
	"proxy_error",
	"geo_unavailable",
	"unknown",
] as const;

export const ProxyCheckErrorCodeSchema = z
	.enum(PROXY_CHECK_ERROR_CODES)
	.describe("Why the check failed");

export type ProxyCheckErrorCode = z.infer<typeof ProxyCheckErrorCodeSchema>;

export const ProxyCheckResultSchema = z
	.object({
		ok: z.boolean().describe("The proxy relayed a request end to end"),
		latencyMs: z
			.number()
			.int()
			.nonnegative()
			.describe("Round trip through the proxy, or time until failure"),
		ip: z.string().optional().describe("Exit IP the geo service saw"),
		countryCode: z
			.string()
			.length(2)
			.optional()
			.describe("ISO 3166-1 alpha-2 country of the exit IP"),
		country: z.string().optional(),
		region: z.string().optional(),
		city: z.string().optional(),
		timezone: z.string().optional().describe("IANA zone of the exit IP"),
		isp: z.string().optional(),
		error: z.string().optional().describe("Friendly reason, when not ok"),
		errorCode: ProxyCheckErrorCodeSchema.optional(),
		checkedAt: z.iso.datetime(),
	})
	.describe("Proxy check result");

export type ProxyCheckResult = z.infer<typeof ProxyCheckResultSchema>;
